/** Telling a question from a search, for the title bar (2026-10-06): a
 *  question gets an answer card above the mail results. */

const QUESTION_START = /^(what|when|where|who|whom|whose|which|how|why|do|does|did|is|are|am|was|were|can|could|should|will|would|have|has|any|tell me|summari[sz]e|give me|remind me|am i|what's|whats|when's|who's|where's)\b/i;

export function isQuestion(text: string | null | undefined): boolean {
  const t = (text ?? "").trim();
  if (t.length < 6) return false;
  return t.endsWith("?") || QUESTION_START.test(t);
}

/** Where a tool's answer lives in WOV, for the card's links. */
export function linksForTools(tools: string[]): Array<{ label: string; to: string }> {
  const out: Array<{ label: string; to: string }> = [];
  const add = (label: string, to: string) => { if (!out.some((l) => l.to === to)) out.push({ label, to }); };
  for (const t of tools) {
    if (t === "wov_list_trips") add("Open Travel", "/travel");
    else if (t === "wov_list_events") add("Open Calendar", "/calendar");
    else if (t === "wov_list_tasks" || t === "wov_get_task" || t === "wov_task_triage") add("Open Tasks", "/tasks");
    else if (t === "wov_triage_queue") add("Open Mail", "/mail");
  }
  return out;
}

/** Questions asked from the title bar, newest first, for the Ask page's
 *  list (2026-10-06). Kept in this browser only; a convenience, not a
 *  record. */
export type AskedQuestion = { q: string; at: number };
const HISTORY_KEY = "wov.ask.history";
const HISTORY_MAX = 40;

export function readAskHistory(): AskedQuestion[] {
  try {
    const raw = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.filter((x): x is AskedQuestion => !!x && typeof x.q === "string" && typeof x.at === "number");
  } catch {
    return [];
  }
}

function writeAskHistory(list: AskedQuestion[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, HISTORY_MAX)));
  } catch {
    /* storage off: the list lasts for this visit only */
  }
  window.dispatchEvent(new Event("wov:ask-history"));
}

/** Put a question at the top of the list (asking it again moves it up). */
export function rememberQuestion(q: string, now = Date.now()): AskedQuestion[] {
  const t = q.trim();
  const list = [{ q: t, at: now }, ...readAskHistory().filter((x) => x.q.toLowerCase() !== t.toLowerCase())].slice(0, HISTORY_MAX);
  writeAskHistory(list);
  return list;
}

export function forgetQuestion(q: string): AskedQuestion[] {
  const list = readAskHistory().filter((x) => x.q !== q);
  writeAskHistory(list);
  return list;
}

/** What the title bar does with what is typed: answer it, or search mail
 *  for it. A question is asked unless you pick Search; anything else is
 *  searched unless you pick Ask. */
export type BoxMode = "ask" | "search";
export function boxMode(text: string, chosen: BoxMode | null): BoxMode {
  return chosen ?? (isQuestion(text) ? "ask" : "search");
}
